import { motion } from 'framer-motion';
import { Heart, Target, Users } from 'lucide-react';

export const About = () => {
  const fadeInUp = {
    initial: { opacity: 0, y: 60 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6 }
  };

  return (
    <div data-testid="about-page" className="min-h-screen">
      <section 
        className="py-32 relative overflow-hidden"
        style={{
          background: 'linear-gradient(135deg, #8B4513 0%, #A0522D 50%, #CD853F 100%)'
        }}
      >
        <div 
          className="absolute inset-0 opacity-[0.04]"
          style={{
            backgroundImage: "url('https://customer-assets.emergentagent.com/job_earthy-interact/artifacts/nz39lewe_IMG_20260129_120644.png')",
            backgroundSize: '600px',
            backgroundPosition: 'center',
            backgroundRepeat: 'repeat'
          }}
        />

        <div className="absolute top-20 right-20 text-white/5 transform rotate-12">
          <svg width="200" height="200" viewBox="0 0 100 100">
            <path d="M50 10 L50 90 M10 50 L90 50" stroke="currentColor" strokeWidth="8" />
          </svg>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div {...fadeInUp} className="text-center mb-16">
            <span className="text-sm font-body font-medium tracking-widest uppercase text-amber-200">About Us</span>
            <h1 className="text-5xl md:text-6xl font-heading font-bold text-white mt-4">
              More Than a Church—A Movement
            </h1>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center mb-24">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="space-y-6"
            >
              <h2 className="text-3xl md:text-4xl font-heading font-bold text-white">
                Where Faith Comes Alive
              </h2>
              <p className="text-lg text-white/95 leading-relaxed">
                Christ Refuge Community Church isn't just where you attend—it's where you belong. We're a diverse family united by one purpose: to know Christ and make Him known. Here, the spiritually hungry are fed, the brokenhearted are healed, and destiny is awakened.
              </p>
              <p className="text-lg text-white/95 leading-relaxed">
                We believe God is raising up a generation of believers who refuse to settle for mediocrity. Whether you're taking your first steps of faith or you're a seasoned warrior, Christ Refuge is where your breakthrough begins and your calling is activated.
              </p>
              <p className="text-lg text-white/95 leading-relaxed">
                <strong className="text-amber-300">This is your moment. This is your refuge. Welcome home.</strong>
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <img
                src="https://customer-assets.emergentagent.com/job_earthy-interact/artifacts/ywouh2zl_CRCC%20-%20Welcome%20Flyer%20-%20Updated%20-%20SMS.png"
                alt="Welcome to Christ Refuge Community Church"
                className="rounded-3xl shadow-2xl w-full h-auto border-4 border-white/20"
              />
            </motion.div>
          </div>
        </div>
      </section>

      <section 
        className="py-20 relative overflow-hidden"
        style={{
          background: 'linear-gradient(135deg, #DEB887 0%, #D2B48C 50%, #BC8F8F 100%)'
        }}
      >
        <div 
          className="absolute inset-0 opacity-[0.03]"
          style={{
            backgroundImage: "url('https://customer-assets.emergentagent.com/job_earthy-interact/artifacts/nz39lewe_IMG_20260129_120644.png')",
            backgroundSize: '500px',
            backgroundPosition: 'center',
            backgroundRepeat: 'repeat'
          }}
        />

        <div className="absolute bottom-10 left-10 text-[#8B4513]/5 transform -rotate-12">
          <svg width="150" height="150" viewBox="0 0 100 100">
            <path d="M50 10 L50 90 M10 50 L90 50" stroke="currentColor" strokeWidth="10" />
          </svg>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-24">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              data-testid="value-card-mission"
              className="bg-white/90 backdrop-blur-sm rounded-3xl p-8 text-center hover:shadow-xl transition-all duration-300 hover:-translate-y-2"
            >
              <div className="w-16 h-16 bg-gradient-to-br from-primary to-amber-600 rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg">
                <Target className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-2xl font-heading font-bold text-foreground mb-4">Our Mission</h3>
              <p className="text-muted-foreground">
                To ignite revival fires, empower every believer, and transform communities through the unstoppable power of God's love.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.1 }}
              data-testid="value-card-vision"
              className="bg-white/90 backdrop-blur-sm rounded-3xl p-8 text-center hover:shadow-xl transition-all duration-300 hover:-translate-y-2"
            >
              <div className="w-16 h-16 bg-gradient-to-br from-secondary to-green-700 rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg">
                <Heart className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-2xl font-heading font-bold text-foreground mb-4">Our Vision</h3>
              <p className="text-muted-foreground">
                A generation ablaze with God's glory—world-changers, history-makers, and spiritual revolutionaries who will carry His presence to every nation.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
              data-testid="value-card-values"
              className="bg-white/90 backdrop-blur-sm rounded-3xl p-8 text-center hover:shadow-xl transition-all duration-300 hover:-translate-y-2"
            >
              <div className="w-16 h-16 bg-gradient-to-br from-accent to-yellow-600 rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg">
                <Users className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-2xl font-heading font-bold text-foreground mb-4">Our Values</h3>
              <p className="text-muted-foreground">
                Radical faith. Authentic community. Unwavering excellence. Boundless compassion. Lives transformed by the power of the Holy Spirit.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      <section 
        data-testid="leadership-section" 
        className="py-20 relative overflow-hidden"
        style={{
          background: 'linear-gradient(135deg, #8B7355 0%, #A0826D 50%, #8B7355 100%)'
        }}
      >
        <div 
          className="absolute inset-0 opacity-[0.04]"
          style={{
            backgroundImage: "url('https://customer-assets.emergentagent.com/job_earthy-interact/artifacts/nz39lewe_IMG_20260129_120644.png')",
            backgroundSize: '700px',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat'
          }}
        />

        <div className="absolute top-20 left-20 text-white/5 transform rotate-45">
          <svg width="180" height="180" viewBox="0 0 100 100">
            <path d="M50 10 L50 90 M10 50 L90 50" stroke="currentColor" strokeWidth="8" />
          </svg>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <span className="text-sm font-body font-medium tracking-widest uppercase text-amber-300">Leadership</span>
            <h2 className="text-4xl md:text-5xl font-heading font-bold text-white mt-4">
              Meet Our Leaders
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              data-testid="leader-card-overseers"
              className="bg-white/90 backdrop-blur-sm rounded-3xl overflow-hidden shadow-2xl hover:shadow-2xl transition-all duration-300 hover:-translate-y-2"
            >
              <div className="h-96 bg-gradient-to-br from-primary/10 to-amber-200/20 flex items-center justify-center">
                <img
                  src="https://customer-assets.emergentagent.com/job_earthy-interact/artifacts/lfgutdcc_IMG_20260131_220736.jpg"
                  alt="Apostles Kenneth & Yolanda Browne"
                  className="w-full h-full object-contain"
                />
              </div>
              <div className="p-8">
                <span className="text-sm font-body font-medium tracking-widest uppercase text-primary">Overseers</span>
                <h3 className="text-3xl font-heading font-bold text-foreground mt-2 mb-4">
                  Bishop Kenneth & Apostle Yolanda Browne
                </h3>
                <p className="text-muted-foreground leading-relaxed">
                  Visionary leaders called by God to raise up spiritual giants. Bishop Kenneth and Apostle Yolanda Browne serve as overseers of Christ Refuge Community Church, bringing decades of prophetic insight, strategic leadership, and a burning passion to see God's people walk in their full potential. Their ministry has transformed countless lives across the nation.
                </p>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
              data-testid="leader-card-pastors"
              className="bg-white/90 backdrop-blur-sm rounded-3xl overflow-hidden shadow-2xl hover:shadow-2xl transition-all duration-300 hover:-translate-y-2"
            >
              <div className="h-96 bg-gradient-to-br from-secondary/10 to-green-100/20 flex items-center justify-center">
                <img
                  src="https://customer-assets.emergentagent.com/job_earthy-interact/artifacts/pkk8lu3x_IMG_5191.jpeg"
                  alt="Pastors Dwayne & Lady Tessa Darden"
                  className="w-full h-full object-contain"
                />
              </div>
              <div className="p-8">
                <span className="text-sm font-body font-medium tracking-widest uppercase text-secondary">Lead Pastors</span>
                <h3 className="text-3xl font-heading font-bold text-foreground mt-2 mb-4">
                  Pastors Dwayne & Lady Tessa Darden
                </h3>
                <p className="text-muted-foreground leading-relaxed">
                  Dynamic shepherds with hearts ablaze for God's presence. Pastors Dwayne and Lady Tessa Darden lead with authenticity, power, and unwavering commitment to seeing every person encounter the life-changing reality of Jesus Christ. Under their leadership, Christ Refuge has become a place where the impossible becomes possible and destinies are rewritten.
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};
